import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LANG, LANGUAGES, isLanguage, makeT } from "./translate";

// The user's language, remembered across visits. Pages that are translated read it; a page that
// isn't translated yet simply doesn't.
const STORAGE_KEY = "aintreel.lang";

const LanguageContext = createContext(null);

function readSaved() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isLanguage(saved) ? saved : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(readSaved);

  const setLang = useCallback((next) => {
    if (!isLanguage(next)) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode or blocked storage: the choice lasts for this visit only
    }
  }, []);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext) || { lang: DEFAULT_LANG, setLang: () => {} };

// A translator for one dictionary in the current language (or in `langOverride`)
export function useT(dict, langOverride) {
  const { lang } = useLanguage();
  const active = isLanguage(langOverride) ? langOverride : lang;
  return useMemo(() => makeT(dict, active), [dict, active]);
}

// <html lang> follows a translated page while it is shown, so screen readers pick the right voice
export function useDocumentLanguage(lang) {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.lang;
    root.lang = (LANGUAGES[lang] || LANGUAGES[DEFAULT_LANG]).html;
    return () => {
      root.lang = previous;
    };
  }, [lang]);
}
